/**
 * SpotifyAuthInterceptor
 *
 * Automatically attaches `Authorization: Bearer <token>` to every outbound
 * request whose URL starts with https://api.spotify.com or
 * https://spclient.wg.spotify.com.
 *
 * Flow:
 *  1. Non-Spotify requests → pass through unchanged (Firebase, ngx-translate, etc.)
 *  2. No stored token       → pass through (SpotifyProfilePage handles unauthenticated state)
 *  3. Token is expired      → proactively refresh, then send with new token
 *  4. Token is valid        → attach and send; on HTTP 401 refresh once and retry
 *
 * Requests to https://accounts.spotify.com (token exchange / refresh) are
 * intentionally excluded from interception to avoid circular logic.
 *
 * Registration: see app.module.ts — provided via HTTP_INTERCEPTORS multi token.
 */

import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { SpotifyAuthService } from '../services/spotify-auth.service';

const SPOTIFY_HOSTS = [
  'https://api.spotify.com',
  'https://spclient.wg.spotify.com',
];

@Injectable()
export class SpotifyAuthInterceptor implements HttpInterceptor {

  private spotifyAuth = inject(SpotifyAuthService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // ── 1. Skip non-Spotify API requests ─────────────────────────────────────
    if (!SPOTIFY_HOSTS.some(h => req.url.startsWith(h))) {
      return next.handle(req);
    }

    // ── 2. Skip when not authenticated ───────────────────────────────────────
    if (!this.spotifyAuth.getStoredAccessToken()) {
      return next.handle(req);
    }

    // ── 3. Proactively refresh if the stored token has expired ────────────────
    if (this.spotifyAuth.isTokenExpired()) {
      return from(this.spotifyAuth.refreshAccessToken()).pipe(
        switchMap(token => {
          // If refresh failed (null), proceed without auth — the page will show an error
          if (!token) return next.handle(req);
          return next.handle(this.withToken(req, token));
        })
      );
    }

    // ── 4. Token is valid — attach and send; handle server-side 401 ──────────
    const token = this.spotifyAuth.getStoredAccessToken()!;
    return next.handle(this.withToken(req, token)).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          // Refresh once and retry; if refresh fails, surface the original error
          return from(this.spotifyAuth.refreshAccessToken()).pipe(
            switchMap(newToken => {
              if (!newToken) return throwError(() => err);
              return next.handle(this.withToken(req, newToken));
            }),
            catchError(() => throwError(() => err))
          );
        }
        return throwError(() => err);
      })
    );
  }

  /** Clones the request with the Authorization header injected. */
  private withToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
